import classnames from 'classnames'
import { connect, DataItem, getDataClass } from 'scrivito'

import { TimerecordActions } from '../myTimes/TimerecordActions'
import { ControlledCheckbox } from '@/Atomic/Atoms/Inputs/ControlledCheckbox'
import { Column, TableRow, TableCell } from '@/Atomic/Templates/Table'
import { DataClassName } from '@/Data/const'
import { EnumValue } from '@/Data/typesV2'
import { ensureString } from '@/utils/ensureString'
import { getDataItem } from '@/utils/getDataItem'
import {
  formatTimerecordDate,
  getStatusColour,
  getStatusTitle,
  getTimerecordDurations,
} from '@/utils/timerecordUtils'

interface TimerecordRowProps {
  dataItem: DataItem
  statusOptions?: EnumValue[]
  isSelected?: boolean
  canEdit?: boolean
  onToggleSelection?: () => void
  onEdit: () => void
  onCopy: () => void
  onDelete: () => void
  onInfo: () => void
  onAdd: () => void
}

const getReferencedDataItem = (
  dataClassName: DataClassName,
  id: string,
): DataItem | null => {
  if (!id) return null

  return getDataItem(getDataClass(dataClassName)!, id).dataItem
}

const getReferencedData = (dataItem: DataItem) => {
  const userId = ensureString(dataItem.get('user'))
  const clientId = ensureString(dataItem.get('client'))
  const orderId = ensureString(dataItem.get('order'))
  const positionId = ensureString(dataItem.get('position'))

  const userDataItem = getReferencedDataItem(
    DataClassName.TIMERECORD_USER,
    userId,
  )

  const clientDataItem = getReferencedDataItem(
    DataClassName.TIMERECORD_CLIENT,
    clientId,
  )

  const orderDataItem = getReferencedDataItem(
    DataClassName.TIMERECORD_ORDER,
    orderId,
  )

  const positionDataItem = getReferencedDataItem(
    DataClassName.TIMERECORD_POSITION,
    positionId,
  )

  return { userDataItem, clientDataItem, orderDataItem, positionDataItem }
}

export const TimerecordRow = connect(
  ({
    dataItem,
    statusOptions = [],
    isSelected = false,
    canEdit = true,
    onToggleSelection,
    onEdit,
    onCopy,
    onDelete,
    onInfo,
    onAdd,
  }: TimerecordRowProps) => {
    const itemId = dataItem.id()

    const recordedAt = formatTimerecordDate(dataItem.get('recordedAt'))

    const description = ensureString(dataItem.get('description'))
    const internalNote = ensureString(dataItem.get('internalNote'))

    const { userDataItem, clientDataItem, orderDataItem, positionDataItem } =
      getReferencedData(dataItem)

    const userName = ensureString(userDataItem?.get('name'))

    const clientName = ensureString(clientDataItem?.get('name'))

    const orderName = ensureString(orderDataItem?.get('name'))

    const positionName = ensureString(positionDataItem?.get('name'))

    const { totalDuration, billableDuration, nonBillableDuration } =
      getTimerecordDurations(dataItem)

    const currentStatusValue = ensureString(dataItem.get('status'))

    const displayStatus = getStatusTitle(
      currentStatusValue,
      statusOptions,
    )?.toUpperCase()

    const statusColour = getStatusColour(displayStatus)

    const handleStatusChange = (
      event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      const newStatusEnumValue = event.target.value
      try {
        dataItem.update({ status: newStatusEnumValue })
      } catch (error) {
        console.error('Failed to update status:', error)
      }
    }

    return (
      <TableRow className="timerecord-row-with-actions">
        <TableCell noWrap className="time-tracking-select-cell">
          {canEdit && (
            <ControlledCheckbox
              aria-label={`Select entry ${recordedAt || itemId}`}
              checked={isSelected}
              onChange={() => onToggleSelection?.()}
            />
          )}
        </TableCell>
        <TableCell noWrap variant={Column.WIDTH_120PX}>
          {recordedAt || '—'}
        </TableCell>
        <TableCell noWrap variant={Column.WIDTH_160PX}>
          {userName || '—'}
        </TableCell>
        <TableCell
          noWrap
          variant={Column.WIDTH_260PX}
          className="timerecord-cell-with-actions"
        >
          <div>{clientName || '—'}</div>
          <div>
            <strong>{orderName || '—'}</strong>
          </div>
          <div>{positionName || '—'}</div>
          <div>{description}</div>
          <div>{internalNote}</div>

          <TimerecordActions
            onInfo={onInfo}
            onCopy={canEdit ? onCopy : undefined}
            onEdit={canEdit ? onEdit : undefined}
            onDelete={canEdit ? onDelete : undefined}
            onAdd={onAdd}
          />
        </TableCell>
        <TableCell noWrap variant={Column.WIDTH_120PX}>
          {totalDuration}
        </TableCell>
        <TableCell noWrap variant={Column.WIDTH_120PX}>
          {billableDuration}
        </TableCell>
        <TableCell noWrap variant={Column.WIDTH_140PX}>
          {nonBillableDuration}
        </TableCell>
        <TableCell noWrap variant={Column.WIDTH_120PX}>
          {displayStatus && statusOptions.length > 0 && !canEdit ? (
            <span className={classnames('badge', 'md', `bg-${statusColour}`)}>
              {displayStatus}
            </span>
          ) : displayStatus && statusOptions.length > 0 ? (
            <select
              value={currentStatusValue}
              onChange={handleStatusChange}
              className={classnames('badge', 'md', `bg-${statusColour}`)}
              style={{
                border: 'none',
                cursor: 'pointer',
                appearance: 'none',
                padding: '4px 8px',
              }}
            >
              {statusOptions.map((enumValue) => (
                <option key={enumValue.value} value={enumValue.value}>
                  {enumValue.title.toUpperCase()}
                </option>
              ))}
            </select>
          ) : (
            '—'
          )}
        </TableCell>
      </TableRow>
    )
  },
)
